import type { StoreProduct } from "./store";

const standaloneBottomText = /\b(?:trousers?|pants?|bottoms?|shalwars?|shawls?|dupattas?|scarves?|leggings?|skirts?)\b/i;
const coordinatedOutfitText = /\b(?:suit|set|outfit|ensemble|co[\s-]?ord)\b/i;
const onePieceGarmentText = /\b(?:shirt|kurta|top|tunic|kameez|kaftan|dress)\b/i;

function productText(product: StoreProduct): string {
  const tags = Array.isArray(product.tags) ? product.tags : product.tags.split(",");
  return [
    product.title,
    product.product_type,
    ...tags,
    ...(product.collection_handles ?? []),
  ].filter(Boolean).join(" ").toLowerCase();
}

function explicitPieceCount(text: string): number | null {
  const match = text.match(/\b(1|one|2|two|3|three)\s*[- ]?\s*(?:piece|pcs?)\b/i);
  if (!match) return null;
  const word = match[1].toLowerCase();
  if (word === "1" || word === "one") return 1;
  if (word === "2" || word === "two") return 2;
  return 3;
}

function isStandaloneBottom(product: StoreProduct, text: string): boolean {
  if (!standaloneBottomText.test(text)) return false;
  if ((product.pieceCount ?? 0) >= 2 || (explicitPieceCount(text) ?? 0) >= 2) return false;
  return !coordinatedOutfitText.test(text) && !onePieceGarmentText.test(text);
}

function piecePriority(product: StoreProduct, text: string): number {
  const count = product.pieceCount ?? explicitPieceCount(text);
  if (count === 2) return 0;
  if (count === 3) return 1;
  if (count === 1 || onePieceGarmentText.test(text)) return 2;
  return 3;
}

/**
 * Orders automatically selected homepage products by outfit piece count,
 * without changing catalog order within each tier or changing brand-page lists.
 */
export function prioritizeHomepageProducts(products: StoreProduct[]): StoreProduct[] {
  const byBrand = new Map<string, Array<{ product: StoreProduct; index: number; priority: number }>>();

  products.forEach((product, index) => {
    const text = productText(product);
    if (isStandaloneBottom(product, text)) return;
    const brand = product.brandSlug?.trim().toLowerCase() || "ethnc";
    const group = byBrand.get(brand) ?? [];
    group.push({ product, index, priority: piecePriority(product, text) });
    byBrand.set(brand, group);
  });

  const groups = [...byBrand.values()].map(group =>
    group.sort((a, b) => a.priority - b.priority || a.index - b.index),
  );
  const ordered: StoreProduct[] = [];
  for (let position = 0; groups.some(group => position < group.length); position += 1) {
    for (const group of groups) {
      const entry = group[position];
      if (entry) ordered.push(entry.product);
    }
  }
  return ordered;
}
